/* eslint-disable no-underscore-dangle */
import React, { useRef } from 'react';
import {
  Button, Form, InputGroup, Spinner,
} from 'react-bootstrap';
import BookingService from '../../../Services/BookingService';
import PaymentsService from '../../../Services/PaymentsService';
import BookingReferenceSearch from './BookingReferenceSearch';
import BookingSuggestions from './BookingSuggestions';
import {
  computeAllocatableLines,
  computeAllocatableOperatorLines,
  describeLine,
  lineKindLabel,
} from './allocatableLines';
import { formatDateTime } from '../../../Utils/timezone';
import { SETTLED_PAYMENT_STATUSES } from '../../../Utils/paymentStatus';
import { paidByOrderLine } from '../../../Utils/orderLinePaid';

// Build a `{order_line_key → already-paid-amount}` map from a
// booking's existing payments. Excludes the payment being edited (if
// any) so the picker doesn't double-count the row that the operator
// is *currently* re-allocating.
//
// Exported for test: this is the money math behind every "(owes €X)"
// label and every auto-allocation pre-fill, so it is unit-tested
// directly rather than only through the modal that renders it.
//
// CUSTOMER SIDE goes through `paidByOrderLine` — the one policy, shared
// with the Invoicing panel and mirrored from ph-core. Read that header
// before touching this. It only used to credit a payment line that
// named an `order_line_key`, so on PU_DASH_5580294 (360 EUR, 180 keyed
// onto `base` and 180 never keyed) the picker offered "owes €360" on a
// line with 180 already in, and pre-filled that amount.
//
// OPERATOR SIDE stays keyed-lines-only, deliberately. Pooling unkeyed
// money across a booking's operator lines is the one thing rule 4 of
// the spec forbids: on a multi-operator booking it pays the WRONG
// operator. The API's own operator-side reader
// (`PaymentsController.outstandingLines`) scopes credits by
// `lines[].operator_id` instead; a row here has no operator chosen yet
// when this runs, so it cannot do the same. Giving the operator side a
// pool means solving that scoping first — do not "make it symmetric".
export function buildPaidByLineKey(payments, booking, excludePaymentId, sideMode) {
  const settled = (payments || []).filter(
    (p) => p && p._id !== excludePaymentId && SETTLED_PAYMENT_STATUSES.has(p.status),
  );

  if (sideMode !== 'operator') {
    // Targets in `order_lines` order, from the same helper that renders the
    // picker — so what the pool pays off is exactly what the operator sees,
    // including a line already settled (includeZeroBalance) whose paid figure
    // the label still needs.
    const targets = computeAllocatableLines(booking, { includeZeroBalance: true })
      .map((l) => ({ key: l._key, owed: l._balanceNum }));
    return paidByOrderLine({ booking, payments: settled, targets }).byKey;
  }

  const map = Object.create(null);
  const bookingId = booking?._id;
  settled.forEach((p) => {
    if (p.counterparty?.kind !== 'operator') return;
    (p.lines || []).forEach((l) => {
      if (!l || l.booking_id !== bookingId || !l.order_line_key) return;
      const amt = Number(l.gross_amount) || 0;
      // out = + (payouts add to what we've paid out), in = - (recoupments
      // offset), so a refund pair nets to zero on its own.
      const sign = p.direction === 'out' ? 1 : -1;
      map[l.order_line_key] = (map[l.order_line_key] || 0) + sign * amt;
    });
  });
  return map;
}

// Shared money-format helper (HUF rounded + 'Ft', everything else
// euro symbol + 2dp). Inlined per-modal originally; consolidated here
// since the allocation rows are the single consumer for the format
// across all three modals.
function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function formatNumberInputDefault(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '';
  const n = Number(amount);
  if (currency === 'HUF') return String(Math.round(n));
  return n.toFixed(2);
}

/** Allocation rows row shape (kept as a JSDoc for the consumer):
 * {
 *   id: string,             // stable per-row id for keys
 *   bookingRef: string,     // current search input text
 *   matchedBooking: object | null,
 *   allocatableLines: array,
 *   orderLineKey: string,   // '' = booking-level (no specific line)
 *   loadingOrderLines: boolean,
 *   orderLinesError: string,
 *   amount: string,
 *   operatorId?: string,    // only when sideMode === 'operator'
 * }
 */
export function newAllocationRow(id, amount = '') {
  return {
    id,
    bookingRef: '',
    matchedBooking: null,
    allocatableLines: [],
    orderLineKey: '',
    loadingOrderLines: false,
    orderLinesError: '',
    amount,
    operatorId: '',
  };
}

export function formatRowAmount(amount, currency) {
  return formatNumberInputDefault(amount, currency);
}

/**
 * Shared "list of payment allocations" UI. Drives the per-row picker
 * across LinkToBookingModal (unmatched-Stripe attach), EditAllocationModal
 * (re-allocate attached), and RecordPaymentModal (manual record).
 *
 * The component is FULLY controlled — the parent owns `rows` and
 * receives every change through `onChange(nextRows)`. State that
 * doesn't need to live in the parent (search-fetch race protection)
 * is kept in refs locally.
 *
 * Props:
 *   - rows:                 array of rows (see shape above)
 *   - onChange(nextRows):   called whenever rows change
 *   - expectedTotal:        the gross amount that rows[].amount must sum to
 *   - currency:             display currency for amount inputs + summary
 *   - sideMode:             'customer' (default) — picker shows customer-side lines
 *                           'operator'           — picker shows operator-side lines
 *                                                  AND each row exposes an operator picker
 *   - disabled:             true while parent is submitting
 *   - operators:            [{ id|Id|_id, name }] required when sideMode='operator'
 *   - loadingOperators:     boolean — passes through to the operator <Select>
 *   - allowOrderLineFreeChoice: when true the picker includes a
 *                              "Booking-level (no line)" option so the
 *                              operator can deliberately not pin to a
 *                              specific line. Defaults to true.
 *   - addRowLabel / pickerLabel: tweakable strings for the three modals
 *                                that frame the same UI slightly
 *                                differently.
 *   - testIdPrefix:         keep per-modal data-testid attribute prefixes
 *                           distinct (e.g. 'ltb', 'ea', 'rpm').
 */
function PaymentAllocationRows({
  rows,
  onChange,
  expectedTotal,
  currency,
  sideMode = 'customer',
  disabled = false,
  operators = [],
  loadingOperators = false,
  allowOrderLineFreeChoice = true,
  addRowLabel = 'Add another allocation',
  pickerLabel = 'Allocate to',
  testIdPrefix = 'par',
  // When the component is operating on an existing payment (edit
  // flow), passing the payment._id here ensures the per-line "owes
  // €X" label doesn't double-count THIS payment's existing allocation
  // against itself.
  excludePaymentId = undefined,
  // Layer-3 defense: the parent payment's counterparty. When
  // counterpartyKind is 'operator' or 'partner', each row's booking
  // search is scoped to bookings under that counterparty so the
  // operator literally cannot pick a booking belonging to a different
  // operator/partner (prod bug `6a191393`). 'booking' / 'system' kinds
  // are not scoped — there's nothing useful to constrain on.
  //
  // For operator-side rows where each row carries its own operatorId,
  // the row-level operatorId takes precedence over the top-level
  // counterpartyId (the row's operator IS the counterparty for that
  // line). Pre-selection rows fall back to counterpartyId.
  counterpartyKind = undefined,
  counterpartyId = undefined,
  // Optional payment signals { amount, currency, date, email, name }. When
  // provided, a "Suggested matches" panel ranks unpaid bookings to one-click
  // attach. Spread through as-is, so a new signal needs no change here.
  matchSignals = null,
}) {
  // Per-row fetch race protection. A slower earlier response must not
  // overwrite a fresher one when the operator changes the booking
  // rapidly. Keyed by row id so each row has its own counter.
  const orderLinesSeqRef = useRef({});

  const isOperatorSide = sideMode === 'operator';
  // `paidByLineKey` decorates allocatable lines with "already paid"
  // info so the picker's "(owes €X)" label reflects REMAINING balance
  // (line.debit − line.credit − already-paid-on-this-line), not the
  // gross debit. Required for the "linking when paid in still shows
  // full amount" fix.
  const computeLinesForBooking = (booking, paidByLineKey = null) => (isOperatorSide
    ? computeAllocatableOperatorLines(booking, { paidByLineKey })
    : computeAllocatableLines(booking, { paidByLineKey }));

  // Helpers that nudge a single row inside the parent's `rows` array.
  // Use the functional form of onChange so async paths (the
  // listForBooking decoration, error handlers) don't stomp rows the
  // operator added between fire-time and resolve-time. Synchronous
  // call sites are unaffected by the functional form.
  const updateRow = (id, patch) => {
    onChange((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeRow = (id) => {
    onChange((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  // Outstanding owed: a single line's remaining balance when a line is chosen,
  // else the booking's total remaining across its lines.
  const outstandingFor = (lines, orderLineKey) => {
    if (orderLineKey) {
      const line = (lines || []).find((l) => l._key === orderLineKey);
      return line && Number.isFinite(line._balanceNum) ? Math.max(0, line._balanceNum) : 0;
    }
    return (lines || []).reduce(
      (s, l) => s + (Number.isFinite(l._balanceNum) ? Math.max(0, l._balanceNum) : 0),
      0,
    );
  };

  // Payment amount still unallocated, excluding `rowId`. Infinity when there's
  // no expected total yet (record flow before an amount is typed) → no cap.
  const remainingForRow = (rowId) => {
    const total = Number(expectedTotal) || 0;
    if (total <= 0) return Infinity;
    const others = rows
      .filter((r) => r.id !== rowId)
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    return Math.max(0, round2(total - others));
  };

  // Target a row's amount at the booking/line outstanding, but never more than
  // what's left of the payment. When there's no per-line outstanding to anchor
  // on (booking-level allocation), fall back to the remaining payment so the
  // operator can still allocate the lump. Returns a number.
  const targetRowAmount = (rowId, lines, orderLineKey) => {
    const outstanding = outstandingFor(lines, orderLineKey);
    const cap = remainingForRow(rowId);
    const remaining = cap === Infinity ? 0 : cap;
    const anchor = outstanding > 0 ? outstanding : remaining;
    const value = cap === Infinity ? anchor : Math.min(anchor, cap);
    return round2(value);
  };

  // Same target, formatted for the amount input.
  const cappedAmount = (rowId, lines, orderLineKey) => (
    formatRowAmount(targetRowAmount(rowId, lines, orderLineKey), currency)
  );

  // Hard cap for a row's amount input. Undefined → no cap.
  const rowMaxAmount = (row) => {
    const max = targetRowAmount(row.id, row.allocatableLines, row.orderLineKey);
    return max > 0 ? max : undefined;
  };

  // Booking-resolved handler with cache + race protection. After
  // loading order_lines, fetches the booking's existing payments so
  // the picker's "owes €X" annotation reflects REMAINING balance
  // (debit − credit − already-paid-via-other-payments) instead of
  // gross debit. Fetches are best-effort; failures degrade gracefully
  // to the no-paid-info view.
  const handleBookingResolved = async (rowId, booking) => {
    if (!booking) {
      updateRow(rowId, {
        matchedBooking: null,
        allocatableLines: [],
        orderLineKey: '',
        loadingOrderLines: false,
        orderLinesError: '',
      });
      return;
    }

    const seq = (orderLinesSeqRef.current[rowId] || 0) + 1;
    orderLinesSeqRef.current[rowId] = seq;

    // Step 1 — show inline order_lines immediately if the search
    // result carried them. The paid-by-line decoration follows asyn-
    // chronously once payments load, so the picker is interactive
    // even while the secondary fetch is in flight.
    const inline = Array.isArray(booking.order_lines) ? booking.order_lines : null;
    let merged = booking;
    if (inline && inline.length > 0) {
      const lines = computeLinesForBooking(booking);
      const lineKey = lines.length > 0 ? lines[0]._key : '';
      updateRow(rowId, {
        matchedBooking: booking,
        allocatableLines: lines,
        orderLineKey: lineKey,
        amount: cappedAmount(rowId, lines, lineKey),
        loadingOrderLines: false,
        orderLinesError: '',
      });
    } else {
      updateRow(rowId, {
        matchedBooking: booking,
        allocatableLines: [],
        orderLineKey: '',
        loadingOrderLines: true,
        orderLinesError: '',
      });
      try {
        const full = await BookingService.getBookingById(booking._id);
        if (orderLinesSeqRef.current[rowId] !== seq) return;
        merged = full || booking;
        const lines = computeLinesForBooking(merged);
        const lineKey = lines.length > 0 ? lines[0]._key : '';
        updateRow(rowId, {
          matchedBooking: merged,
          allocatableLines: lines,
          orderLineKey: lineKey,
          amount: cappedAmount(rowId, lines, lineKey),
          loadingOrderLines: false,
          orderLinesError: '',
        });
      } catch (err) {
        if (orderLinesSeqRef.current[rowId] !== seq) return;
        const lines = computeLinesForBooking(booking);
        const lineKey = lines.length > 0 ? lines[0]._key : '';
        updateRow(rowId, {
          allocatableLines: lines,
          orderLineKey: lineKey,
          amount: cappedAmount(rowId, lines, lineKey),
          loadingOrderLines: false,
          orderLinesError: err.response?.data?.message
            || err.message || 'Could not load order lines — falling back to booking total',
        });
        return;
      }
    }

    // Step 2 — decorate with already-paid amounts so the "(owes €X)"
    // label reflects what's actually left on each line. Best-effort:
    // failure here just leaves the original gross-debit display, no
    // user-facing error.
    try {
      const bookingPayments = await PaymentsService.listForBooking(merged._id);
      if (orderLinesSeqRef.current[rowId] !== seq) return;
      const paidByLineKey = buildPaidByLineKey(
        bookingPayments,
        merged,
        excludePaymentId,
        sideMode,
      );
      const linesWithPaid = computeLinesForBooking(merged, paidByLineKey);
      // Snap orderLineKey to a still-allocatable line. The prior
      // default may have been fully paid (and dropped from the list);
      // fall through to the first remaining line.
      //
      // Use the FUNCTIONAL form of onChange (prev => ...) so we read
      // the freshest rows when the async fetch returns. If we mapped
      // the closure-captured `rows` here, a row the operator added
      // via "Add another allocation" between the booking attach and
      // this listForBooking response would get silently dropped from
      // the result. All three parent modals pass React's setRows
      // directly so the functional form works.
      onChange((prev) => prev.map((r) => {
        if (r.id !== rowId) return r;
        const stillValid = linesWithPaid.some((l) => l._key === r.orderLineKey);
        // Refine the lines (paid-aware "(owes €X)" labels) and snap the key, but
        // DON'T touch the amount here — the synchronous attach already defaulted
        // it, and re-setting it asynchronously would stomp a manual edit.
        return {
          ...r,
          allocatableLines: linesWithPaid,
          orderLineKey: stillValid ? r.orderLineKey : (linesWithPaid[0]?._key || ''),
        };
      }));
    } catch {
      // Silent: keep the un-decorated picker. Operator can still
      // allocate; they just won't see "remaining after prior payments"
      // hint in the dropdown.
    }
  };

  const totalAllocated = round2(rows.reduce((sum, r) => {
    const n = Number(r.amount);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0));
  const delta = round2(Number(expectedTotal || 0) - totalAllocated);
  const sumMatches = Math.abs(delta) < 0.005;
  const allBookingsResolved = rows.every((r) => r.matchedBooking);

  // Distinctness check is (booking_id, order_line_key) — two rows
  // hitting the same exact line should collapse into one.
  const tuples = rows
    .filter((r) => r.matchedBooking)
    .map((r) => `${r.matchedBooking._id}::${r.orderLineKey || ''}`);
  const distinctRows = new Set(tuples).size === tuples.length;

  const addRow = () => {
    onChange((prev) => [...prev, newAllocationRow(
      `row-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      delta > 0 ? formatNumberInputDefault(delta, currency) : '',
    )]);
  };

  // Attach a suggested booking: fill the first empty row, else add a fresh one.
  // The suggestion carries only summary fields, so handleBookingResolved fetches
  // the full booking (and its order_lines) via getBookingById.
  const handlePickSuggestion = (suggestion) => {
    const booking = {
      _id: suggestion.booking_id,
      booking_reference: suggestion.booking_reference,
    };
    const empty = rows.find((r) => !r.matchedBooking && !r.bookingRef);
    if (empty) {
      updateRow(empty.id, { bookingRef: booking.booking_reference || '' });
      handleBookingResolved(empty.id, booking);
    } else {
      const id = `row-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      onChange((prev) => [...prev, {
        ...newAllocationRow(id, delta > 0 ? formatNumberInputDefault(delta, currency) : ''),
        bookingRef: booking.booking_reference || '',
      }]);
      handleBookingResolved(id, booking);
    }
  };

  // Attach several bookings at once — reuse empty rows first, then append. Build
  // the rows array once, then resolve each (order lines + amount). Accepts full
  // booking objects (with `_id`, ideally `order_lines`) so both the suggestions
  // panel and the booking-search multi-select feed the same path.
  const attachMany = (bookings) => {
    if (!bookings || bookings.length === 0) return;
    const usedEmpty = new Set();
    const additions = [];
    const newRows = [...rows];
    bookings.forEach((booking, i) => {
      const candidate = newRows.find(
        (r) => !r.matchedBooking && !r.bookingRef && !usedEmpty.has(r.id),
      );
      let id;
      if (candidate) {
        id = candidate.id;
        usedEmpty.add(id);
        const ci = newRows.findIndex((r) => r.id === id);
        newRows[ci] = { ...newRows[ci], bookingRef: booking.booking_reference || '' };
      } else {
        id = `row-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 5)}`;
        newRows.push({ ...newAllocationRow(id, ''), bookingRef: booking.booking_reference || '' });
      }
      additions.push({ id, booking });
    });
    onChange(() => newRows);
    additions.forEach(({ id, booking }) => handleBookingResolved(id, booking));
  };

  // Suggestions carry summary fields; map them to the booking shape attachMany
  // (and handleBookingResolved) expect.
  const handlePickMany = (suggestions) => attachMany(
    (suggestions || []).map((s) => ({
      _id: s.booking_id,
      booking_reference: s.booking_reference,
    })),
  );

  const attachedBookingIds = rows
    .filter((r) => r.matchedBooking)
    .map((r) => r.matchedBooking._id);

  // Once a booking is attached we know the customer's email — feed it back so
  // the panel surfaces that customer's OTHER unpaid bookings (works even when
  // the payment itself carried no payer email).
  const attachedEmail = rows.map((r) => r.matchedBooking?.email_address).find(Boolean);
  const suggestionSignals = matchSignals
    ? { ...matchSignals, email: matchSignals.email || attachedEmail }
    : null;

  return (
    <div data-testid={`${testIdPrefix}-rows`}>
      {suggestionSignals && !disabled && (
        <BookingSuggestions
          signals={suggestionSignals}
          onPick={handlePickSuggestion}
          onPickMany={handlePickMany}
          excludeIds={attachedBookingIds}
        />
      )}
      {rows.map((row, idx) => (
        <div
          key={row.id}
          className="border rounded p-3 mb-2"
          data-testid={`${testIdPrefix}-row-${idx}`}
        >
          <div className="d-flex align-items-center justify-content-between mb-2">
            <small className="text-muted">
              Allocation
              {' '}
              {idx + 1}
            </small>
            {rows.length > 1 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => removeRow(row.id)}
                className="text-danger p-0"
                type="button"
                disabled={disabled}
                aria-label={`Remove allocation ${idx + 1}`}
                data-testid={`${testIdPrefix}-remove-${idx}`}
              >
                <i className="fa fa-times" />
                {' Remove'}
              </Button>
            )}
          </div>

          {/* Booking picker — once a booking is attached, show it as
              a pill with a "Change booking" affordance, mirroring the
              edit-allocation UX. Search input only shows pre-attach. */}
          {row.matchedBooking ? (
            <div className="small mb-2">
              <div className="p-2 border rounded bg-light d-flex justify-content-between align-items-center">
                <div>
                  <strong>
                    {row.matchedBooking.booking_reference || row.matchedBooking._id}
                  </strong>
                  {row.matchedBooking.full_name && (
                    <span className="text-muted ms-2">
                      {row.matchedBooking.full_name}
                    </span>
                  )}
                  <div className="small text-muted">
                    {row.matchedBooking.date_datetime
                      ? formatDateTime(row.matchedBooking.date_datetime)
                      : '—'}
                    {' · Total '}
                    {formatMoney(
                      row.matchedBooking.total_price ?? row.matchedBooking.price,
                      row.matchedBooking.currency,
                    )}
                  </div>
                </div>
                <Button
                  variant="link"
                  size="sm"
                  className="text-muted p-0"
                  type="button"
                  disabled={disabled}
                  onClick={() => updateRow(row.id, {
                    matchedBooking: null,
                    allocatableLines: [],
                    orderLineKey: '',
                  })}
                >
                  Change booking
                </Button>
              </div>
            </div>
          ) : (
            <>
              <BookingReferenceSearch
                value={row.bookingRef}
                onChange={(v) => updateRow(row.id, { bookingRef: v })}
                onResolve={(booking) => handleBookingResolved(row.id, booking)}
                onResolveMany={attachMany}
                autoFocus={idx === 0 && !row.matchedBooking}
                disabled={disabled}
                // Scope the dropdown to bookings under the parent payment's
                // counterparty. For operator-side rows the per-row
                // `row.operatorId` (when chosen) wins over the umbrella
                // counterpartyId — otherwise the operator can't search
                // until they've picked an operator. For partner-kind
                // payments the partner is always the umbrella id.
                scopeOperatorId={(() => {
                  if (isOperatorSide && row.operatorId) return row.operatorId;
                  if (counterpartyKind === 'operator') return counterpartyId;
                  return undefined;
                })()}
                scopePartnerId={counterpartyKind === 'partner' ? counterpartyId : undefined}
              />
              {/* When the operator has TYPED text but hasn't yet
                  clicked a search result, the row looks superficially
                  ready but `matchedBooking` is null so submit stays
                  disabled. Surface that explicitly — "I see you typed
                  an email, now click one of the results below to
                  actually attach the booking" — instead of letting the
                  operator stare at a disabled button. */}
              {!!row.bookingRef?.trim() && !row.matchedBooking && (
                <div className="small text-warning mt-1" role="alert">
                  <i className="fa fa-exclamation-triangle me-1" />
                  Type a reference, name, email or phone — then
                  {' '}
                  <strong>click a result</strong>
                  {' '}
                  below to attach the booking.
                </div>
              )}
            </>
          )}

          {/* Operator picker (only for sideMode='operator' rows) —
              every operator-side allocation must be tagged with the
              receiving operator so multi-operator bookings settle
              correctly. */}
          {isOperatorSide && row.matchedBooking && (
            <Form.Group className="mt-2" controlId={`${row.id}-operator`}>
              <Form.Label className="small mb-1">Operator</Form.Label>
              <Form.Select
                size="sm"
                value={row.operatorId || ''}
                onChange={(e) => updateRow(row.id, { operatorId: e.target.value })}
                disabled={disabled || loadingOperators}
                data-testid={`${testIdPrefix}-operator-${idx}`}
              >
                <option value="">
                  {loadingOperators ? 'Loading…' : 'Select an operator…'}
                </option>
                {operators.map((o) => {
                  const id = o.id || o.Id || o._id;
                  return (
                    <option key={id} value={id}>
                      {o.name || id}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          )}

          {row.matchedBooking && (
            <Form.Group className="mt-2" controlId={`${row.id}-orderline`}>
              <Form.Label className="small mb-1">{pickerLabel}</Form.Label>
              {row.loadingOrderLines ? (
                <div className="small text-muted d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading order lines…
                </div>
              ) : (
                <>
                  {row.allocatableLines.length > 0 ? (
                    <Form.Select
                      size="sm"
                      value={row.orderLineKey}
                      onChange={(e) => updateRow(row.id, {
                        orderLineKey: e.target.value,
                        amount: cappedAmount(row.id, row.allocatableLines, e.target.value),
                      })}
                      disabled={disabled}
                      data-testid={`${testIdPrefix}-orderline-${idx}`}
                    >
                      {allowOrderLineFreeChoice && (
                        <option value="">— Booking level (no specific line) —</option>
                      )}
                      {row.allocatableLines.map((line) => (
                        <option key={line._key} value={line._key}>
                          {`${lineKindLabel(line)} — ${describeLine(line)}`}
                          {Number.isFinite(line._balanceNum) && line._balanceNum > 0
                            ? ` (owes ${formatMoney(line._balanceNum, line.currency || row.matchedBooking.currency)})`
                            : ''}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <div className="small text-muted" data-testid={`${testIdPrefix}-noline-${idx}`}>
                      {isOperatorSide
                        ? 'No operator-side lines on this booking — payout will be allocated at booking level.'
                        : 'No customer-side lines with an outstanding balance on this booking — payment will be allocated at the booking level.'}
                    </div>
                  )}
                  {row.orderLinesError && (
                    <div className="small text-warning mt-1" role="alert">
                      {row.orderLinesError}
                    </div>
                  )}
                </>
              )}
            </Form.Group>
          )}

          <Form.Group className="mt-2" controlId={`${row.id}-amount`}>
            <Form.Label className="small mb-1">Amount</Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="number"
                min="0"
                max={rowMaxAmount(row)}
                step={currency === 'HUF' ? '1' : '0.01'}
                value={row.amount}
                onChange={(e) => updateRow(row.id, { amount: e.target.value })}
                disabled={disabled}
                data-testid={`${testIdPrefix}-amount-${idx}`}
              />
              <InputGroup.Text>{currency || 'EUR'}</InputGroup.Text>
            </InputGroup>
          </Form.Group>
        </div>
      ))}

      <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={addRow}
          type="button"
          disabled={disabled}
          data-testid={`${testIdPrefix}-add-row`}
        >
          <i className="fa fa-plus me-1" />
          {addRowLabel}
        </Button>
        <div className="small" data-testid={`${testIdPrefix}-allocation-summary`}>
          Allocated
          {' '}
          <strong>{formatMoney(totalAllocated, currency)}</strong>
          {' / '}
          <strong>{formatMoney(Number(expectedTotal || 0), currency)}</strong>
          {!sumMatches && (
            <span className={delta > 0 ? 'text-warning ms-2' : 'text-danger ms-2'}>
              (
              {delta > 0 ? 'remaining ' : 'over by '}
              {formatMoney(Math.abs(delta), currency)}
              )
            </span>
          )}
          {sumMatches && allBookingsResolved && (
            <span className="text-success ms-2">
              <i className="fa fa-check" />
            </span>
          )}
        </div>
      </div>

      {!distinctRows && allBookingsResolved && (
        <div className="small text-danger mb-2" role="alert">
          Two rows target the same booking AND order line. Combine them into
          one row with the summed amount, or pick a different order line on
          one of the duplicates.
        </div>
      )}
    </div>
  );
}

// Validation helper for parents — same checks the component visualises
// internally, exposed so parents can drive submit disabled-state from
// a single source of truth.
export function validateAllocationRows(rows, expectedTotal, { sideMode = 'customer' } = {}) {
  const allBookingsResolved = rows.length > 0 && rows.every((r) => r.matchedBooking);
  const allAmountsPositive = rows.every((r) => {
    const n = Number(r.amount);
    return Number.isFinite(n) && n > 0;
  });
  const total = round2(rows.reduce((sum, r) => {
    const n = Number(r.amount);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0));
  const delta = round2(Number(expectedTotal || 0) - total);
  const sumMatches = Math.abs(delta) < 0.005;
  const tuples = rows
    .filter((r) => r.matchedBooking)
    .map((r) => `${r.matchedBooking._id}::${r.orderLineKey || ''}`);
  const distinctRows = new Set(tuples).size === tuples.length;
  const allOperatorsAssigned = sideMode !== 'operator'
    || rows.every((r) => !r.matchedBooking || !!r.operatorId);
  return {
    canSubmit: allBookingsResolved
      && allAmountsPositive
      && sumMatches
      && distinctRows
      && allOperatorsAssigned,
    allBookingsResolved,
    allAmountsPositive,
    sumMatches,
    distinctRows,
    allOperatorsAssigned,
    delta,
    total,
  };
}

// Serialiser — turn the controlled `rows` into the lines[] payload the
// backend expects on /payments POST/edit-allocation/reassign.
export function rowsToLinesPayload(rows, { sideMode = 'customer' } = {}) {
  return rows.map((r) => {
    const entry = {
      booking_id: r.matchedBooking._id,
      gross_amount: Number(r.amount),
    };
    if (r.orderLineKey) entry.order_line_key = r.orderLineKey;
    if (sideMode === 'operator' && r.operatorId) entry.operator_id = r.operatorId;
    return entry;
  });
}

export default PaymentAllocationRows;
